import cloneDeep from 'lodash/cloneDeep';
import { Img } from './img';
import { Transform } from './transform';
import { Modal } from 'antd';
import { Text } from './text';

const { confirm } = Modal;

const PrintAreaAttributes = {
  fill: 'none',
  stroke: '#cc0000',
  strokeDasharray: 1,
  strokeWidth: 0.5,
  id: 'printArea',
};

const Mapping = {
  CanvasData: (data) => {
    data = cloneDeep(data);

    return {
      blankUri: data.blankUri,
      templateUri: data.templateUri,
      arts: data.arts,
      texts: data.texts,
      area: data.area,
      canvasID: data.canvasID,
      aspectRatio: 1,
      baseHeight: 1000,
    };
  }
};

export const Canvas = class {
  $Base;
  $Blank;
  $Template;
  $Arts;
  $ArtTransformers;
  $Texts;
  $TextTransformers;
  $PrintArea;
  $originalArts;
  $canvasData;
  onChange;
  onTextChange;
  loading;

  constructor(_data) {
    this.$Base = null;
    this.$Blank = null;
    this.$Template = null;
    this.$Arts = [];
    this.$ArtTransformers = [];
    this.$Texts = [];
    this.$TextTransformers = [];
    this.$PrintArea = null;
    this.$originalArts = _data.arts;
    this.$canvasData = Mapping.CanvasData(_data);
    this.onChange = _data.onChange;
    this.onTextChange = _data.onTextChange;
    this.loading = true;
  }

  create() {
    this._loader(true);

    window.addEventListener('resize', this._debounce(this._redraw, this, 150));

    return new Promise((resolve, reject) => {
      setTimeout(() => this._placeBase().then(resolve).catch(reject), 30);
    }).finally(() => (this._loader(false)));
  }

  redrawArea(area) {
    this.$canvasData = {
      ...this.$canvasData,
      area
    };

    this._redraw(this);
  }

  redrawText(texts) {
    this.$canvasData = {
      ...this.$canvasData,
      texts: texts
    };

    this._redraw(this);
  }

  redrawBlank(blankUri, templateUri) {
    this.$canvasData = {
      ...this.$canvasData,
      blankUri
    };

    this._loader(true);

    if (templateUri === this.$canvasData.templateUri) {
      this.$Blank.update(this.$canvasData.blankUri).then(() => this._loader(false));
      return;
    }

    this.$canvasData = {
      ...this.$canvasData,
      templateUri
    };

    Promise.all([
      this.$Blank.update(this.$canvasData.blankUri),
      this.$Template.update(this.$canvasData.templateUri)
    ]).then(() => {
      this._loader(false);
    });
  }

  addArts(data) {
    this._loader(true);

    if (!data?.length) {
      return;
    }

    this.$Template?.remove();

    this.$canvasData.arts = [...data];

    this._removeAllArts();

    const artsPromises = this.$canvasData.arts?.map((art, index) => {
      this.$Arts.push(new Img(art.uri, this.$Base));
      return this.$Arts[index].get();
    });

    Promise.all(artsPromises).then(() => {
      this.$originalArts = cloneDeep(this.$canvasData.arts);

      this._placeArts();

      this._loader(false);
      this._redraw(this);
    });
  }

  removeArt(index) {
    confirm({
      icon: null,
      className: 'warn-confirm-modal',
      closable: true,
      title: 'Delete',
      content: 'Do you really want to delete?',
      okText: 'Delete',
      zIndex: 1020,
      okButtonProps: { size: 'large' },
      cancelButtonProps: { size: 'large', type: 'link' },
      onOk: () => this._removeArt()
    });
  }

  getPNG() {
    return new Promise((resolve, reject) => {
      const id = this.$canvasData.canvasID;

      this.transformerGrid('hide');
      const svgString = new XMLSerializer().serializeToString(document.getElementById(id));

      const canvas = document.createElement('canvas');
      canvas.width = this.$Blank.Width;
      canvas.height = this.$Blank.Height;
      const ctx = canvas.getContext('2d');
      const DOMURL = window.URL || window.webkitURL || window;
      const svg = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const url = (DOMURL as any).createObjectURL(svg);
      const img = new Image();
      img.src = url;
      this.transformerGrid('show');
      img.onload = function () {
        ctx.drawImage(img, 0, 0);
        const png = canvas.toDataURL('image/png');
        (DOMURL as any).revokeObjectURL(png);
        resolve(png);
      };
      img.onerror = (e) => {
        return reject('Error creating image');
      };
    });
  }

  _onChangeHandler(index, moved) {
    const art = this._outputArt(index);

    if (!art) {
      return;
    }

    art.moved = moved;

    this.$canvasData.arts[index] = art;

    if (this.onChange) {
      this.onChange(art, index);
    }
  }

  _onTextChangeHandler(index, moved?: boolean) {
    let text = this.$Texts[index] && this.$TextTransformers[index] ? this.$TextTransformers[index].getPosition().art : null;
    const area = this.$canvasData.area;

    if (text) {
      const ratio = this.$canvasData.aspectRatio;
      text = {
        ...this.$canvasData.texts[index],
        ...{
          x: Math.round(text.x / ratio - area.x),
          y: Math.round(text.y / ratio - area.y),
          w: Math.round(text.w / ratio),
          h: Math.round(text.h / ratio),
          fontSize: Math.round(text.h / this.$Texts[index].Height * this.$canvasData.texts[index].fontSize),
          moved: moved || false
        }
      };

      text.x  = text.x < 0 ? 0 : text.x;
      text.y  = text.y < 0 ? 0 : text.y;

      this.$Texts[index].Height = text.h * ratio;
      this.$Texts[index].Width = text.w * ratio;
    }

    this.$canvasData.texts[index] = text;

    if (this.onTextChange) {
      this.onTextChange(text, index);
    }
  }

  _placeBase() {
    this.$Base = (window as any).Snap(`#${this.$canvasData.canvasID}`);

    this.$Blank = new Img(this.$canvasData.blankUri, this.$Base);
    this.$Template = new Img(this.$canvasData.templateUri, this.$Base);

    return new Promise((resolve, reject) => {
      Promise.all([this.$Blank.get(), this.$Template.get()]).then(() => {
        // Must be first
        this.$canvasData.aspectRatio = this.$Blank.AspectRatio;

        if (this.$Blank.loadingFail) {
          reject({ mess: 'Blank Image loading fail!' });
        }

        this.$Blank.place();

        if (!this.$Base) {
          return;
        }

        this.$Base.animate(
          {
            height: this.$Blank.NaturalHeight * this.$Blank.AspectRatio,
            width: this.$Blank.NaturalWidth * this.$Blank.AspectRatio
          },
          250,
          (window as any).mina.linear,
          () => {
            this.$canvasData.baseHeight = this.$Blank.NaturalHeight;

            this.$PrintArea = this._printArea();

            if (!this.$PrintArea) {
              reject({ mess: 'Print Area not defined!' });
              return;
            }

            this._createArts().then(resolve).catch(reject);
          }
        );
      });
    });
  }

  _redraw(_self) {
    if (_self.loading || !_self.$Base) {
      return;
    }

    _self.$Blank.redraw();
    _self.$canvasData.aspectRatio = _self.$Blank.AspectRatio;
    _self.$Base.attr({
      height: _self.$Blank.NaturalHeight * _self.$Blank.AspectRatio,
      width: _self.$Blank.NaturalWidth * _self.$Blank.AspectRatio
    });
    _self.$canvasData.baseHeight = _self.$Blank.NaturalHeight;
    _self.$PrintArea = _self._printArea();
    _self._redrawArts();
  }

  /*
   ** TEXT
   */
  _placeTexts() {
    if (!this.$canvasData.texts?.length) {
      return;
    }

    this.$Template.remove();

    if (this.$ArtTransformers?.length) {
      this.$ArtTransformers.forEach((transformer) => transformer.destroy());

      this._plugArtTransformers();
    } else {
      this.$Template.place({ w: this.$Blank.NaturalWidth, h: this.$Blank.NaturalHeight, ratio: this.$Blank.AspectRatio });
      if (this.$Template?.El?.node) {
        this.$Template.El.node.style.pointerEvents = 'none';
      }
    }

    this._removeAllTexts();

    this.$canvasData.texts?.forEach((t, index) => {
      this.$Texts.push(new Text({ ...t, fontSize: t.fontSize * this.$canvasData.aspectRatio }, this.$Base));

      if (!this.$Texts[index]?.text) {
        return;
      }

      if (t.moved && t.w) {
        this.$Texts[index].place({
          x: (t.x + this.$canvasData.area.x) * this.$canvasData.aspectRatio,
          y: (t.y + this.$canvasData.area.y) * this.$canvasData.aspectRatio,
        });
      } else {
        this.$Texts[index].text.moved = false;
        this.$Texts[index].place();
      }

      const transformParams = {
        handleRotate: false,
        handleRemove: false,
        mask: this.$PrintArea ? this.$PrintArea : false,
        boundary: this.$PrintArea ? this.$PrintArea : false,
        onChange: () => this._onTextChangeHandler(index, true),
      };

      this.$TextTransformers[index] = new Transform(this.$Texts[index].El, transformParams);
      this.$TextTransformers[index].plug();

      this._onTextChangeHandler(index, t.moved || false);
    });
  }

  /*
   ** ART
   */
  _createArts() {
    if (!this.$canvasData.arts?.every((art) => art?.uri)) {
      return Promise.reject();
    }

    this._removeAllArts();

    this.$canvasData.arts?.forEach((art) => {
      this.$Arts.push(new Img(art.uri, this.$Base));
    });

    const promises = this.$Arts.map(($Art) => {
      return new Promise((resolve, reject) => {
        $Art.get().then(() => {
          if (!$Art || $Art.loadingFail) {
            reject('');
          }

          resolve(null);
        });
      });
    });

    return Promise.all(promises).then(() => {
      this._placeArts();
    });
  }

  _placeArts() {
    if (!this.$Arts?.length) {
      return;
    }

    this.$canvasData.arts?.forEach((art, index) => {
      const artParams = this._inputArt(art);
      this.$Arts[index].place(artParams);
    });

    this._plugArtTransformers();
  }

  _plugArtTransformers() {
    if (!this.$Template) {
      return;
    }

    this.$Template.place({ w: this.$Blank.NaturalWidth, h: this.$Blank.NaturalHeight, ratio: this.$Blank.AspectRatio });
    if (this.$Template?.El?.node) {
      this.$Template.El.node.style.pointerEvents = 'none';
    }

    this.$ArtTransformers = [];

    this.$canvasData.arts?.forEach((art, index) => {
      const transformParams = {
        maxSize: {
          w: art?.maxW * this.$canvasData.aspectRatio,
          h: art?.maxH * this.$canvasData.aspectRatio
        },
        handleRotate: true,
        handleRemove: false,
        mask: this.$PrintArea ? this.$PrintArea : false,
        boundary: this.$PrintArea ? this.$PrintArea : false,
        onChange: () => this._onChangeHandler(index,true),
        remove: () => this.removeArt(index)
      };

      this.$ArtTransformers.push(new Transform(this.$Arts[index].El, transformParams));
      this.$ArtTransformers[index].plug();
    });
  }

  _destroyArt() {
    if (!this.$ArtTransformers?.length || !this.$Arts?.length) {
      return;
    }

    this.$Template?.remove();
    this.$canvasData.arts = [];

    this._removeAllArts();
  }

  _removeArt() {
    this._destroyArt();
    this._onChangeHandler(0, true);
  }

  _redrawArts() {
    this._loader(true);
    this.$Template?.remove();

    this._removeAllArts();
    this._removeAllTexts();

    const artsPromises = this.$canvasData.arts?.map((art, index) => {
      this.$Arts.push(new Img(art.uri, this.$Base));
      return this.$Arts[index].get();
    });

    Promise.all(artsPromises).then(() => {
      this.$originalArts = cloneDeep(this.$canvasData.arts);

      this._loader(false);

      this._placeArts();
      this._placeTexts();
    });
  }

  _inputArt(art) {
    const area = this.$canvasData.area;
    const ratio = this.$canvasData.aspectRatio;

    return {
      x: (art.x + area.x) * ratio,
      y: (art.y + area.y) * ratio,
      w: art.w,
      h: art.h,
      a: art.a || 0,
      ratio: ratio
    };
  }

  _outputArt(index) {
    let art = this.$Arts?.length && this.$ArtTransformers?.length ? this.$ArtTransformers[index]?.getPosition()?.art : null;
    if (art) {
      const ratio = this.$canvasData.aspectRatio;
      const area = this.$canvasData.area;
      art = {
        ...this.$canvasData.arts[index],
        ...{
          x: Math.round(art.x / ratio - area.x),
          y: Math.round(art.y / ratio - area.y),
          w: Math.round(art.w / ratio),
          h: Math.round(art.h / ratio),
          a: this._round(art.a)
        }
      };
    }
    return art;
  }

  _printArea() {
    const area = this.$canvasData.area;

    if (this.$PrintArea) {
      this.$PrintArea.remove();
    }

    if (!area) {
      return false;
    }

    const multiplier = this.$canvasData.aspectRatio * this.$canvasData.baseHeight / area.previewHeight;
    const inscribedArea = {
      x: area.x * multiplier,
      y: area.y * multiplier,
      w: area.w * multiplier,
      h: area.h * multiplier,
    };

    return this.$Base.rect(inscribedArea.x, inscribedArea.y, inscribedArea.w, inscribedArea.h).attr(PrintAreaAttributes);
  }

  /*
   ** TRANSFORMER
   */
  transformerGrid(action = '') {
    this.$ArtTransformers.forEach(($transformer) => {
      if (!$transformer) {
        return;
      }

      const isHidden = action == 'hide' ? false : action == 'show' ? true : $transformer.isHidden();
      if (isHidden) {
        $transformer.show();
      } else {
        $transformer.hide();
      }
      this.$PrintArea.attr({ opacity: isHidden ? 1 : 0 });
    });
  }

  _loader(loading: boolean): void {
    this.loading = loading;

    if (loading) {
      (window as any).bbApp.UI.block(document.querySelector(`#${this.$canvasData.canvasID}`)?.parentElement);
      return;
    }

    (window as any).bbApp.UI.unblock(document.querySelector(`#${this.$canvasData.canvasID}`)?.parentElement);
  }

  _debounce(func, arg, time = 100) {
    let timer;
    return function (event) {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => func(arg), time, event);
    };
  }

  _round(num) {
    const m = Number((Math.abs(num) * 100).toPrecision(15));
    return (Math.round(m) / 100) * Math.sign(num);
  }

  _removeAllTexts() {
    this.$TextTransformers?.forEach((transformer) => transformer.destroy());
    this.$TextTransformers = [];
    this.$Texts?.forEach((text) => text.remove());
    this.$Texts = [];
  }

  _removeAllArts() {
    this.$ArtTransformers.forEach((at) => at.destroy());
    this.$ArtTransformers = [];
    this.$Arts.forEach((a) => a.remove());
    this.$Arts = [];
  }
};
